import { FireConfig, ManualAsset } from '../types';

export interface CanonicalNetWorthBreakdown {
    total: number;
    assets: {
        portfolio: number;
        manual: number;
        pensions: number;
        cash: number;
        property: number;
    };
    liabilities: number;
}

const sumAssetsByType = (assets: ManualAsset[], types: ManualAsset['type'][]): number =>
    assets.filter((asset) => types.includes(asset.type)).reduce((sum, asset) => sum + (asset.value || 0), 0);

/**
 * Builds a single source of truth for net worth that can be shared across the app.
 * Formula: portfolioValue + manualAssets + pensions + cash + property − liabilities
 */
export const buildCanonicalNetWorth = (config: FireConfig, portfolioValue: number): CanonicalNetWorthBreakdown => {
    const manualAssets = config.manualAssets || [];
    const liabilities = config.liabilities || [];

    const cashTotal = sumAssetsByType(manualAssets, ['Cash']);
    const propertyTotal = sumAssetsByType(manualAssets, ['Real Estate']);
    const pensionsTotal = sumAssetsByType(manualAssets, ['Pension']);

    // Manual assets that aren't counted in the explicit categories above
    const manualOtherTotal = manualAssets
        .filter((asset) => !['Cash', 'Real Estate', 'Pension'].includes(asset.type))
        .reduce((sum, asset) => sum + (asset.value || 0), 0);

    const liabilitiesTotal = liabilities.reduce((sum, liability) => sum + (liability.balance || 0), 0);

    const total =
        portfolioValue + manualOtherTotal + pensionsTotal + cashTotal + propertyTotal - liabilitiesTotal;

    return {
        total,
        assets: {
            portfolio: portfolioValue,
            manual: manualOtherTotal,
            pensions: pensionsTotal,
            cash: cashTotal,
            property: propertyTotal,
        },
        liabilities: liabilitiesTotal,
    };
};

